import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { ToastProvider } from './context/ToastContext';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Hero from './components/sections/Hero';
import Categories from './components/sections/Categories';
import ProductGrid from './components/sections/ProductGrid';
import Features from './components/sections/Features';
import Newsletter from './components/sections/Newsletter';
import LoginModal from './components/auth/LoginModal';
import Toast from './components/ui/Toast';
import './App.css';

function App() {
  // State management للتطبيق
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Handle search functionality
  const handleSearch = (query) => {
    setSearchQuery(query);
    setSelectedCategory(null); // Clear category when searching
    
    // إذا كان هناك بحث، انتقل لقسم المنتجات
    if (query && query.trim()) {
      setTimeout(() => {
        const productsSection = document.getElementById('products');
        if (productsSection) {
          productsSection.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 100);
    }
  };

  // Handle category selection
  const handleCategorySelect = (categoryName) => {
    setSelectedCategory(categoryName);
    setSearchQuery(''); // Clear search when selecting category
    
    // انتقل لقسم المنتجات عند اختيار فئة
    setTimeout(() => {
      const productsSection = document.getElementById('products');
      if (productsSection) {
        productsSection.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedCategory(null);
  };

  // Handle login modal
  const handleOpenLogin = () => {
    setLoginModalOpen(true);
  };

  const handleCloseLogin = () => {
    setLoginModalOpen(false);
  };

  // Handle scroll to sections
  const handleScrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };

  // Page transition variants for Framer Motion
  const pageVariants = {
    initial: { 
      opacity: 0,
      y: 20
    },
    animate: { 
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    },
    exit: { 
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.3
      }
    }
  };

  // Container variants for staggered animations
  const containerVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const sectionVariants = {
    initial: { opacity: 0, y: 30 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <ToastProvider>
      <AuthProvider>
        <CartProvider>
          <motion.div
            className="min-h-screen bg-gray-50"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {/* Header Component */}
            <Header 
              onSearch={handleSearch}
              searchQuery={searchQuery}
              onLoginRequired={handleOpenLogin}
            />
            
            {/* Main Content */}
            <motion.main
              variants={containerVariants}
              initial="initial"
              animate="animate"
            >
              {/* Hero Section */}
              <motion.section 
                id="home" 
                className="relative"
                variants={sectionVariants}
              >
                <Hero 
                  onGetStarted={handleOpenLogin}
                  onScrollToProducts={() => handleScrollToSection('products')}
                  onScrollToCategories={() => handleScrollToSection('categories')}
                />
              </motion.section>
              
              {/* Categories Section */}
              <motion.section 
                id="categories" 
                className="py-16 bg-white"
                variants={sectionVariants}
              >
                <Categories 
                  onCategorySelect={handleCategorySelect}
                  selectedCategory={selectedCategory}
                />
              </motion.section>
              
              {/* Products Section */}
              <motion.section 
                id="products" 
                className="py-16 bg-gray-50"
                variants={sectionVariants}
              >
                <ProductGrid 
                  onLoginRequired={handleOpenLogin}
                  searchQuery={searchQuery}
                  selectedCategory={selectedCategory}
                  onClearFilters={handleClearFilters}
                />
              </motion.section>
              
              {/* Features Section */}
              <motion.section 
                id="features" 
                className="py-16 bg-white"
                variants={sectionVariants}
              >
                <Features />
              </motion.section>
              
              {/* Newsletter Section */}
              <motion.section 
                id="newsletter" 
                className="py-16 bg-gradient-to-br from-blue-50 to-purple-50"
                variants={sectionVariants}
              >
                <Newsletter />
              </motion.section>

              {/* Additional Section - About (if needed) */}
              <motion.section 
                id="about" 
                className="py-16 bg-white"
                variants={sectionVariants}
              >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <div className="text-center">
                    <motion.h2 
                      className="text-4xl font-bold text-gray-900 mb-8"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6 }}
                    >
                      About ShopHub
                    </motion.h2>
                    <motion.p 
                      className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                    >
                      ShopHub is your premier destination for quality products and exceptional shopping experiences. 
                      We curate the finest selection of items across multiple categories, ensuring that every 
                      purchase meets our high standards of quality and value.
                    </motion.p>
                  </div>
                </div>
              </motion.section>
            </motion.main>

            {/* Footer Component */}
            <Footer />

            {/* Modals and Overlays */}
            <LoginModal
              isOpen={loginModalOpen}
              onClose={handleCloseLogin}
              initialMode="login"
            />

            {/* Toast Notifications */}
            <Toast />

            {/* Loading Overlay (if needed) */}
            {isLoading && (
              <motion.div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="bg-white rounded-2xl p-8 shadow-2xl">
                  <div className="flex items-center space-x-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="text-lg font-medium text-gray-900">Loading...</span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Scroll to Top Button */}
            <motion.button
              className="fixed bottom-8 right-8 z-40 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.1, y: -2 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              style={{ 
                display: 'block' // يمكنك إضافة منطق لإخفاء/إظهار الزر حسب موضع التمرير
              }}
            >
              <svg 
                className="w-6 h-6" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M5 10l7-7m0 0l7 7m-7-7v18" 
                />
              </svg>
            </motion.button>

            {/* Debug Info (يمكن إزالتها في الإنتاج) */}
            {process.env.NODE_ENV === 'development' && (
              <div className="fixed bottom-4 left-4 bg-black text-white p-2 rounded-lg text-xs font-mono opacity-70 z-50">
                <div>Search: "{searchQuery}"</div>
                <div>Category: {selectedCategory || 'None'}</div>
                <div>Login Modal: {loginModalOpen ? 'Open' : 'Closed'}</div>
              </div>
            )}
          </motion.div>
        </CartProvider>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
